/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable dxp-rules/no-inline-css */
import { FC, useEffect, useMemo, useState } from 'react';

import { useApplication } from '@eneco/common/application';
import RichText from '@eneco/components/RichText/RichText';
import {
  deleteEnecoBeXapiSiteApiV1OrdersByOrderIdPromo,
  putEnecoBeXapiSiteApiV1OrdersByOrderIdFinalize,
  putEnecoBeXapiSiteApiV1OrdersByOrderIdPromo,
} from '@eneco/dc-be/client/services.gen';
import type { PriceSimulationProductCombinationOptionDto, PromoDto } from '@eneco/dc-be/client/types.gen';
import { useFormatter } from '@eneco/i18n';
import {
  Accordion,
  AlertDialog,
  Bucket,
  Button,
  ButtonLink,
  Checkbox,
  Heading,
  InputText,
  NotificationBox,
  Stack,
  Text,
  TextLink,
} from '@eneco/sparky';
import { CalendarIcon, CheckIcon, EuroIcon, HomeIcon, ProfileIcon, TruckIcon } from '@eneco/sparky/icons';
import { NotificationBoxProps } from '@eneco/sparky/types';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { OrderManagementWithItems } from '@sitecore/types/manual/be/OrderManagementWithItems';
import { useRouter } from 'next/router';
import { Controller, useFormContext } from 'react-hook-form';
import { useSWRConfig } from 'swr';

import DataErrorNotification from '../../../components/DataErrorNotification';
import { LoadingListSpinner } from '../../../components/Loading/LoadingListSpinner';
import ReadFormField from '../../../components/ReadFormField';
import { useDateFormatter } from '../../../hooks/useDateFormatter';
import { formattedAddress } from '../../../utils/addresses/addressFormatter';
import { buildNavigationHref } from '../../../utils/buildNavigationHref';
import { link } from '../../../utils/richTextReplacements';
import { MembershipNumberInput } from '../../my-eneco/profile/contact-details/gezinsbond/MembershipNumberInput';
import { SelectedProduct, SelectedServices } from '../components/SelectedProductCards';
import { StyledDisclaimerText } from '../components/StyledDisclaimerText';
import { useOrderId } from '../hooks/useOrderId';
import { useSelectedProducts } from '../hooks/useSelectedProducts';
import { OrderManagementFormSchema, PROFESSIONAL_USER_OPTION_YES } from '../types/orderManagementSchema';
import { FlowChildProps, OrderManagementStep, orderManagementSteps } from '../types/OrderManagementSteps';
import { getTrackableFormValues } from '../utils/getTrackableFormValues';
import useCheckLossOfPromo from '../utils/useCheckLossOfPromo';

export const OverviewStep: FC<FlowChildProps> = ({ nextMainStep }) => {
  const { fields } = useContent<OrderManagementWithItems>();
  const { query } = useRouter();
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [promoResult, setPromoResult] = useState<NotificationBoxProps | null>(null);
  const [hasUpdateError, setHasUpdateError] = useState(false);
  const orderId = useOrderId();
  const { trackEventBE } = useTracking();
  const { longDate } = useDateFormatter();
  const priceSimulationId = query.priceSimulationId as string | undefined;
  const {
    trigger,
    formState: { errors },
    watch,
    register,
    control,
    getValues,
  } = useFormContext<OrderManagementFormSchema>();
  const { language } = useApplication();

  const personalDetails = watch('personalDetailsStep');
  const selectPaymentMethod = watch('selectPaymentMethod');
  const deliveryAddressStep = watch('deliveryAddressStep');

  const {
    hasSelectedProducts,
    hasSelectedServices,
    hasSelectedMaintenance,
    hasSelectedServiceProposal,
    selectedProducts,
    orderPromos,
    isLoading,
    isWelcomePromo,
    orderSimulateError,
    orderDataError,
  } = useSelectedProducts({
    orderId: orderId ?? '',
  });

  const isBundle = !!(selectedProducts?.electricityProduct?.productName && selectedProducts?.gasProduct?.productName);
  const hasDualFuelPromoWithSingleProduct = orderPromos?.[0]?.dualFuelFlag && !isBundle;

  const { mutate } = useSWRConfig();

  useEffect(() => {
    if (!orderId || !hasDualFuelPromoWithSingleProduct) return;
    (async () => {
      const { response } = await deleteEnecoBeXapiSiteApiV1OrdersByOrderIdPromo({
        path: { orderId },
      });
      if (response.ok) {
        mutate(`/orders/${orderId}`);
        setPromoResult({
          title: fields?.overviewStep?.promoCodeInvalidNotification?.value?.title,
          text: <RichText html={fields?.overviewStep?.promoCodeInvalidNotification?.value?.content} />,
          variant: fields?.overviewStep?.promoCodeInvalidNotification?.value?.variant ?? 'error',
          isAlert: true,
        });
      }
    })();
  }, [orderId, hasDualFuelPromoWithSingleProduct, mutate, fields?.overviewStep?.promoCodeInvalidNotification]);

  const { hasLossOfPromo } = useCheckLossOfPromo({ paymentMethod: selectPaymentMethod?.paymentMethod });

  const promoDisclaimers = useMemo(() => {
    return (orderPromos ?? []).map(promo =>
      language === 'nl' ? (promo.offerDisclaimerNl ?? '') : (promo.offerDisclaimerFr ?? ''),
    );
  }, [orderPromos, language]);

  const onNextClick = async () => {
    setIsLoadingSubmit(true);
    setHasUpdateError(false);
    trackEventBE('be_om_check_confirmation', {
      data: getTrackableFormValues(getValues()),
    });
    const isValid = await trigger('overviewStep');
    if (!isValid || !orderId) {
      setIsLoadingSubmit(false);
      return;
    }
    try {
      const { response } = await putEnecoBeXapiSiteApiV1OrdersByOrderIdFinalize({
        path: { orderId },
        body: { privacyPolicyAccepted: true, dataProcessingAccepted: true },
      });

      if (response.ok) {
        mutate(`/orders/${orderId}`);
        nextMainStep();
      } else {
        setHasUpdateError(true);
      }
    } catch {
      setHasUpdateError(true);
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  return (
    <Stack gap="10" alignX="start">
      {hasUpdateError && <DataErrorNotification />}
      <Stack gap="2">
        <Heading as="h2" size="M">
          {fields?.overviewStep?.title?.value}
        </Heading>
        <RichText html={fields?.overviewStep?.subTitle?.value} />
      </Stack>

      <Stack gap={6} direction="column">
        <Bucket title={fields?.overviewStep?.bucketTitle?.value}>
          <Bucket.Content>
            <Stack gap={6}>
              <Stack direction="row" gap="2" alignX="justify" alignY="start">
                <ReadFormField
                  label={
                    <Stack alignX="start" alignY="center" direction="row" gap={2}>
                      <ProfileIcon /> <span>{fields?.overviewStep?.personalDetailsLabel?.value}</span>
                    </Stack>
                  }
                  value={
                    <Stack direction="column">
                      <Text color="textLowEmphasis">{`${fields?.overviewStep?.nameLabel?.value} ${personalDetails?.firstName} ${personalDetails?.lastName}`}</Text>
                      <Text color="textLowEmphasis">
                        {`${fields?.overviewStep?.dateOfBirthLabel?.value} ${
                          personalDetails?.dateOfBirth ? longDate(personalDetails.dateOfBirth) : ''
                        }`}
                      </Text>
                      <Text color="textLowEmphasis">{`${fields.overviewStep?.phoneLabel?.value} ${personalDetails?.phoneNumber}`}</Text>
                      <Text color="textLowEmphasis">{`${fields.overviewStep?.emailLabel?.value} ${personalDetails?.email}`}</Text>
                    </Stack>
                  }
                />
                <div
                  style={{ alignSelf: 'flex-start' }}
                  onClick={() => handleChangeClick({ step: orderManagementSteps.personalDetailsStep })}>
                  <TextLink color="textLowEmphasis">{fields.selectedProductsStep.changeLink.value.text}</TextLink>
                </div>
              </Stack>

              <Stack direction="row" gap="2" alignX="justify" alignY="start">
                <ReadFormField
                  label={
                    <Stack alignX="start" alignY="center" direction="row" gap={2}>
                      <HomeIcon /> <span>{fields.overviewStep?.addressDetailsLabel?.value}</span>
                    </Stack>
                  }
                  value={
                    <Stack direction="column">
                      <Text color="textLowEmphasis">{`${formattedAddress({
                        street: deliveryAddressStep?.street,
                        houseNumber: deliveryAddressStep?.houseNumber?.toString(),
                        bus: deliveryAddressStep?.busNumber,
                      })}`}</Text>

                      <Text color="textLowEmphasis">{`${formattedAddress({
                        postalCode: deliveryAddressStep?.postalCode,
                        municipality: deliveryAddressStep?.city,
                      })}`}</Text>
                    </Stack>
                  }
                />
                <div
                  style={{ alignSelf: 'flex-start' }}
                  onClick={() => handleChangeClick({ step: orderManagementSteps.deliveryAddressStep })}>
                  <TextLink color="textLowEmphasis">{fields.selectedProductsStep.changeLink.value.text}</TextLink>
                </div>
              </Stack>

              <Stack direction="row" gap="2" alignX="justify" alignY="start">
                <ReadFormField
                  label={
                    <Stack alignX="start" alignY="center" direction="row" gap={2}>
                      <EuroIcon /> <span>{fields.overviewStep?.paymentMethodLabel?.value}</span>
                    </Stack>
                  }
                  value={
                    <Text color="textLowEmphasis">
                      {
                        fields.paymentMethodStep?.paymentMethodsList?.value?.options?.find(
                          pm => pm?.name === selectPaymentMethod?.paymentMethod,
                        )?.label
                      }
                    </Text>
                  }
                />
                <div
                  style={{ alignSelf: 'flex-start' }}
                  onClick={() => handleChangeClick({ step: orderManagementSteps.selectPaymentMethodStep })}>
                  <TextLink color="textLowEmphasis">{fields.selectedProductsStep.changeLink.value.text}</TextLink>
                </div>
              </Stack>

              {personalDetails?.professionalUser === PROFESSIONAL_USER_OPTION_YES && (
                <Stack direction="row" gap="2" alignX="justify" alignY="start">
                  <ReadFormField
                    label={
                      <Stack alignX="start" alignY="center" direction="row" gap={2}>
                        <TruckIcon /> <span>{fields.overviewStep?.companyDetailsLabel?.value}</span>
                      </Stack>
                    }
                    value={
                      <Stack direction="column">
                        <Text>{personalDetails?.companyName}</Text>
                        <Text>{personalDetails?.VATNumber}</Text>
                      </Stack>
                    }
                  />
                  <div
                    style={{ alignSelf: 'flex-start' }}
                    onClick={() => handleChangeClick({ step: orderManagementSteps.personalDetailsStep })}>
                    <TextLink color="textLowEmphasis">{fields.selectedProductsStep.changeLink.value.text}</TextLink>
                  </div>
                </Stack>
              )}

              {hasSelectedProducts && (
                <Stack direction="row" gap="2" alignX="justify" alignY="start">
                  <ReadFormField
                    label={
                      <Stack alignX="start" alignY="center" direction="row" gap={2}>
                        <CalendarIcon /> <span>{fields.overviewStep?.deliveryStartDateLabel?.value}</span>
                      </Stack>
                    }
                    value={<CommodityStartDates selectedProducts={selectedProducts!} />}
                  />
                  <div
                    style={{ alignSelf: 'flex-start' }}
                    onClick={() =>
                      handleChangeClick({
                        step: selectedProducts?.electricityProduct?.productName
                          ? orderManagementSteps.electricityDetailsStep
                          : orderManagementSteps.gasDetailsStep,
                      })
                    }>
                    <TextLink color="textLowEmphasis">{fields.selectedProductsStep.changeLink.value.text}</TextLink>
                  </div>
                </Stack>
              )}
            </Stack>
          </Bucket.Content>
        </Bucket>
        {(orderSimulateError || orderDataError) && <DataErrorNotification />}
        {hasSelectedProducts && <SelectedProduct />}
        {hasSelectedServices && <SelectedServices />}
        {isLoading ? (
          <LoadingListSpinner />
        ) : hasSelectedProducts || hasSelectedServices ? (
          <NotificationBox
            isAlert={false}
            variant="info"
            text={fields.overviewStep.notificationPriceIndicativeText?.value}
            title={fields.overviewStep.notificationPriceIndicativeTitle?.value}
          />
        ) : (
          <NotificationBox
            isAlert={false}
            variant={fields.overviewStep.emptyBasketNotification?.value?.variant ?? 'info'}
            text={
              <RichText
                html={
                  fields.overviewStep.emptyBasketNotification?.value?.content ??
                  '[not in sitecore]: Winkelmandje is leeg'
                }
              />
            }
            title={
              fields.overviewStep.emptyBasketNotification?.value?.title ?? '[not in sitecore]: Winkelmandje is leeg'
            }
          />
        )}

        <Accordion>
          <Accordion.Item heading={fields.overviewStep?.promotionTitle?.value}>
            <PromotionCodeInput
              orderPromo={orderPromos?.[0] ?? null}
              isWelcomePromo={isWelcomePromo}
              onPromoResult={setPromoResult}
              hasPriceSimulationPromoDisclaimer={
                !hasLossOfPromo && !!selectedProducts?.totalYearlyPriceWithNonCondtitionalPromo
              }
            />
          </Accordion.Item>
          {hasSelectedProducts && (
            <Accordion.Item heading={fields.overviewStep?.gezinsbondTitle?.value}>
              <Stack gap={4}>
                <Text size={'BodyM'}>
                  <RichText html={fields.overviewStep?.gezinsbondDescription?.value} />
                </Text>
                <Stack gap={2} direction={{ initial: 'column', md: 'row' }} alignY="center" alignX="start">
                  <Stack.Item grow>
                    <Controller
                      control={control}
                      name="overviewStep.gezinsbondMembershipNumber"
                      render={({ field: { onChange, value, name, onBlur } }) => (
                        <MembershipNumberInput
                          label={fields.overviewStep?.gezinsbondMembershipNumberText?.value}
                          name={name}
                          placeholder="XXX-XXX-XXX"
                          error={errors.overviewStep?.gezinsbondMembershipNumber?.message?.toString()}
                          value={value}
                          onChange={onChange}
                          onBlur={onBlur}
                          hint={fields.overviewStep?.gezinsbondMembershipNumberText?.value}
                        />
                      )}
                    />
                  </Stack.Item>
                </Stack>
              </Stack>
            </Accordion.Item>
          )}
        </Accordion>
        {promoResult && <NotificationBox {...promoResult} />}
        {(hasSelectedProducts || hasSelectedMaintenance || hasSelectedServiceProposal) && (
          <Checkbox
            label={
              (
                <>
                  <RichText html={fields.overviewStep?.tosStartDescription?.value} />
                  {hasSelectedProducts && (
                    <>
                      {'\u00A0'}
                      <RichText html={fields.overviewStep?.tosCommoditiesDescription?.value} />
                    </>
                  )}
                  {hasSelectedMaintenance && (
                    <>
                      {'\u00A0'}
                      <RichText html={fields.overviewStep?.tosMaintenanceDescription?.value} />
                    </>
                  )}
                  {hasSelectedServiceProposal && (
                    <>
                      {'\u00A0'}
                      <RichText html={fields.overviewStep?.tosAssistanceDescription?.value} />
                    </>
                  )}
                  {'\u00A0'}
                  <RichText html={fields.overviewStep?.tosEndDescription?.value} />
                </>
              ) as any
            }
            error={errors.overviewStep?.acceptTermsAndConditionsCheckbox?.message?.toString()}
            {...register('overviewStep.acceptTermsAndConditionsCheckbox')}
          />
        )}
      </Stack>
      {errors.root && <NotificationBox isAlert={true} variant="error" text={errors.root.message} />}
      {hasSelectedServices || hasSelectedProducts ? (
        <Button onClick={onNextClick} isLoading={isLoadingSubmit}>
          {fields.navigation.confirmButtonLabel?.value}
        </Button>
      ) : (
        <ButtonLink
          action="primary"
          href={
            fields.navigation?.priceSimulationLink?.value?.href
              ? buildNavigationHref(fields.navigation.priceSimulationLink.value.href, {
                  queryParams: {
                    ...(priceSimulationId ? { priceSimulationId } : {}),
                    ...(orderId ? { orderId } : {}),
                  },
                })
              : undefined
          }>
          {fields.overviewStep.emptyBasketButtonLabel?.value ?? '[note in sitecore]: Producten bekijken'}
        </ButtonLink>
      )}

      {promoDisclaimers.map((disclaimer, idx) => (
        <StyledDisclaimerText key={idx}>
          <RichText html={disclaimer} />
        </StyledDisclaimerText>
      ))}
    </Stack>
  );
};

const CommodityStartDates: FC<{
  selectedProducts: PriceSimulationProductCombinationOptionDto;
}> = ({ selectedProducts }) => {
  const { watch } = useFormContext<OrderManagementFormSchema>();
  const { fields } = useContent<OrderManagementWithItems>();
  const { longDate } = useDateFormatter();
  const gasDetails = watch('gasDetailsStep');
  const electricityDetails = watch('electricityDetailsStep');

  const ElecStartDate = () => (
    <Text color="textLowEmphasis">
      {electricityDetails?.startDateQuestion === 'chooseStartDate' && electricityDetails?.startDate
        ? longDate(electricityDetails.startDate as Date)
        : fields.overviewStep.asSoonAsPossibleLabel?.value}
    </Text>
  );

  const GasStartDate = () => (
    <Text color="textLowEmphasis">
      {gasDetails?.startDateQuestion === 'chooseStartDate' && gasDetails?.startDate
        ? longDate(gasDetails.startDate as Date)
        : fields.overviewStep.asSoonAsPossibleLabel?.value}
    </Text>
  );

  const doesElecAndGasHaveSameStartDate = electricityDetails?.startDate === gasDetails?.startDate;
  const hasSelectedElectricityProducts = Boolean(selectedProducts?.electricityProduct?.productName);
  const hasSelectedGasProducts = Boolean(selectedProducts?.gasProduct?.productName);

  if (hasSelectedElectricityProducts && hasSelectedGasProducts && !doesElecAndGasHaveSameStartDate)
    return (
      <Stack direction="column">
        <Stack direction="row" alignY="center" gap={2}>
          <Text>{fields.overviewStep.electricityLabel?.value}</Text> <ElecStartDate />
        </Stack>
        <Stack direction="row" alignY="center" gap={2}>
          <Text>{fields.overviewStep.gasLabel?.value}</Text> <GasStartDate />
        </Stack>
      </Stack>
    );

  if (electricityDetails?.startDate && hasSelectedElectricityProducts) return <ElecStartDate />;
  if (gasDetails?.startDate && hasSelectedGasProducts) return <GasStartDate />;

  return '-';
};

const PromotionCodeInput: FC<{
  orderPromo: PromoDto | null;
  isWelcomePromo: boolean;
  onPromoResult: (result: NotificationBoxProps | null) => void;
  hasPriceSimulationPromoDisclaimer: boolean;
}> = ({ orderPromo, isWelcomePromo, onPromoResult, hasPriceSimulationPromoDisclaimer }) => {
  const { fields } = useContent<OrderManagementWithItems>();
  const { register, getValues, watch } = useFormContext<OrderManagementFormSchema>();
  const orderId = useOrderId();
  const { language } = useApplication();
  const { format } = useFormatter();
  const [showDialog, setShowDialog] = useState(false);
  const [hasUpdateError, setHasUpdateError] = useState(false);
  const { trackEventBE } = useTracking();

  const { mutate } = useSWRConfig();

  const promo = watch('overviewStep.promotionCode');

  const handleApplyPromotionCode = async ({ skipDialog = false }: { skipDialog?: boolean }) => {
    const offerNumber = getValues('overviewStep.promotionCode');

    if (hasPriceSimulationPromoDisclaimer && !skipDialog) {
      setShowDialog(true);
      return;
    }

    try {
      const { response, error } = await putEnecoBeXapiSiteApiV1OrdersByOrderIdPromo({
        path: { orderId: orderId ?? '' },
        body: { offerNumber: offerNumber ?? '' },
      });
      if (response.ok) {
        mutate(`/orders/${orderId}`);
        mutate(`/orders/${orderId}/simulate`);
        onPromoResult(null);
      } else {
        const validationErrors = (error?.errors?.errors as { Validation?: string[] })?.Validation;
        const overviewStep = fields.overviewStep;

        const makeLink = (href: string) =>
          `<a href="${href}" target="_self">${overviewStep.editPromoIssueText.value}</a>`;

        const promoNotification = (
          notif: { title?: string; content?: string; variant?: string } | null | undefined,
          href?: string,
        ): NotificationBoxProps => ({
          title: notif?.title,
          text: href ? (
            <RichText html={format(notif?.content ?? '', { link: makeLink(href) })} replacements={link} />
          ) : (
            <RichText html={notif?.content} />
          ),
          variant: (notif?.variant ?? 'error') as NotificationBoxProps['variant'],
          isAlert: true,
        });

        const selectProductsHref = overviewStep?.selectProductsLink?.value?.href;

        const promoErrorMap: Partial<Record<string, NotificationBoxProps>> = {
          'Order.Promo.NotActive': promoNotification(overviewStep?.promoCodeExpiredNotification?.value),
          'Order.Promo.NotFound': promoNotification(overviewStep?.promoCodeInvalidNotification?.value),
          'Order.Promo.RequiresEBilling': promoNotification(
            overviewStep?.promoCodeRequiresEBillingNotification?.value,
            `#${orderManagementSteps.personalDetailsStep}`,
          ),
          'Order.Promo.InvalidPaymentMethod': promoNotification(
            overviewStep?.promoCodeInvalidPaymentMethodNotification?.value,
            `#${orderManagementSteps.selectPaymentMethodStep}`,
          ),
          'Order.Promo.InvalidCustomerType': promoNotification(
            overviewStep?.promoCodeInvalidCustomerTypeNotification?.value,
          ),
          'Order.Promo.InvalidEnergyType': promoNotification(
            overviewStep?.promoCodeInvalidEnergyTypeNotification?.value,
            selectProductsHref,
          ),
          'Order.Promo.InvalidDualFuel': promoNotification(
            overviewStep?.promoCodeInvalidDualFuelNotification?.value,
            selectProductsHref,
          ),
        };

        const matchedError = validationErrors?.find(e => e in promoErrorMap);

        trackEventBE('be_om_step_back', {
          data: { offerNumber, matchedError },
        });

        onPromoResult(
          promoErrorMap[matchedError ?? ''] ?? promoNotification(overviewStep?.promoCodeInvalidNotification?.value),
        );
      }
    } catch {
      setHasUpdateError(true);
    }
  };

  const handleRemovePromotionCode = async () => {
    try {
      const { response } = await deleteEnecoBeXapiSiteApiV1OrdersByOrderIdPromo({
        path: { orderId: orderId ?? '' },
      });
      if (response.ok) {
        onPromoResult({
          title: fields.overviewStep?.promoCodeRemovalNotification?.value?.title,
          text: <RichText html={fields.overviewStep?.promoCodeRemovalNotification?.value?.content} />,
          variant: (fields.overviewStep?.promoCodeRemovalNotification?.value?.variant ??
            'success') as NotificationBoxProps['variant'],
          isAlert: true,
        });
        mutate(`/orders/${orderId}`);
        mutate(`/orders/${orderId}/simulate`);
      }
    } catch {
      setHasUpdateError(true);
    }
  };

  return (
    <Stack direction="column" gap={4}>
      {hasUpdateError && <DataErrorNotification />}
      {orderPromo && !isWelcomePromo ? (
        <Stack direction="row" gap={2} alignY="start">
          <Stack.Item shrink={false}>
            <CheckIcon color="feedbackSuccess" />
          </Stack.Item>
          <Stack.Item grow>
            <Text size={'BodyM'}>
              {language === 'nl' ? (orderPromo?.offerTextNl ?? '') : (orderPromo?.offerTextFr ?? '')}
            </Text>
          </Stack.Item>
          <TextLink onClick={() => handleRemovePromotionCode()}>
            {fields.overviewStep?.promoCodeRemoveButtonText?.value}
          </TextLink>
        </Stack>
      ) : (
        <Text size={'BodyM'}>
          <RichText html={fields.overviewStep?.promotionDescription?.value} />
        </Text>
      )}
      <Stack direction="row" gap={2} alignY="end">
        <Stack.Item grow>
          <InputText
            label={fields.overviewStep?.promotionCodeFormField?.value?.label}
            placeholder={fields.overviewStep?.promotionCodeFormField?.value?.placeholder}
            {...register('overviewStep.promotionCode')}
          />
        </Stack.Item>
        <Button onClick={() => handleApplyPromotionCode({ skipDialog: false })} size="compact">
          {fields.overviewStep?.promoCodeApplyButtonText?.value}
        </Button>
      </Stack>
      {showDialog && (
        <AlertDialog
          title={fields.overviewStep?.promoCodeCombinationInvalidDialog?.value?.title}
          description={
            <RichText
              html={format(fields.overviewStep?.promoCodeCombinationInvalidDialog?.value?.content ?? '', {
                promo: promo ?? '',
              })}
            />
          }
          isOpen={showDialog}
          setOpen={setShowDialog}
          confirmText={fields.overviewStep?.promoCodeCombinationInvalidDialog?.value?.submitButtonText ?? ''}
          denyText={fields.overviewStep?.promoCodeCombinationInvalidDialog?.value?.cancelButtonText ?? ''}
          onConfirm={() => {}}
          // this is weird, but in the design the cancel button is the apply button
          onDeny={() => handleApplyPromotionCode({ skipDialog: true })}
        />
      )}
    </Stack>
  );
};

const handleChangeClick = ({ step }: { step?: OrderManagementStep }) => {
  window.location.hash = `#${step}`;
};
